import React, { useEffect, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { useSelector } from 'react-redux';
import { Entity, addressToString } from '@debbie/common/dist/index.js';
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { DEFAULT_FILE_NAME, EditorToolbarOptions, } from '@debbie/common/dist/constants.js';
import { getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { patchUser } from '@debbie/cortex/dist/api/customers.js';
import { findRecipient, genRecipientsRaw, } from '@debbie/common/dist/interactions/index.js';
import { EBoksRecipientType } from '@debbie/common/dist/interactions/eboks.js';
import keys from '@debbie/common/dist/cache/keys.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { getMentionSuggestions } from '@debbie/common/dist/templates/suggestions.js';
import { cancelDebounce, debounce } from '@debbie/common/dist/util/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import * as templatesAPI from '@debbie/cortex/dist/api/templates.js';
import { APIBASE } from '@debbie/cortex/dist/config.js';
import { selectEBoks } from '@debbie/cortex/dist/newInteractions/selectors.js';
import { Button, Checkbox, Input, Intent, Modal } from '..//index.js';
import { getSubjectContent, parseText } from '../../utils/templates.js';
import Info from '../Icons/Info.js';
import LetterPreview from './LetterPreview.js';
import MentionExplanation from './MentionExplanation.js';
import TemplateSearch from './TemplateSearch.js';
import { toHtml } from './utils.js';
import Disclaimer from '../Disclaimer.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import q from '@debbie/cortex/dist/query.js';
import { customersEntity } from '@debbie/cortex/dist/customers/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { filterPropertyTypes } from '@debbie/common/dist/properties/index.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
const EBoksInteraction = (props) => {
    const lang = useSelector((s) => s.lang);
    const eboks = useSelector((s) => selectEBoks(s, props.referenceId));
    const tenant = useSelector(selectTenant);
    const propertyTypesLoadable = usePropertyTypes();
    const propertyTypes = h.hasData(propertyTypesLoadable)
        ? propertyTypesLoadable.data
        : undefined;
    const [eboksPreviewModalOpen, setEBoksPreviewModalOpen] = useState(false);
    const [mentionExplanationModalOpen, setMentionExplanationModalOpen] = useState(false);
    const [visibleToClients, setVisibleToClients] = useState(true);
    const [reset, setReset] = useState(false);
    const [content, setContent] = useState(eboks.editorState);
    const [subject, setSubject] = useState(eboks.subjectEditorState);
    useEffect(() => {
        if (reset) {
            setContent(eboks.editorState);
            setSubject(eboks.subjectEditorState);
            setReset(false);
        }
    }, [eboks.editorState, eboks.subjectEditorState]);
    const creditorLoadable = useCreditor(props.case?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const mentionSuggestions = getMentionSuggestions(lang, TemplateType.EBOKS, propertyTypes
        ? filterPropertyTypes(propertyTypes, Entity.Case, 'case_groups', props.case?.caseGroup?.id || null)
        : undefined, tenant);
    const collectorLoadable = useCollector((props.case &&
        creditor &&
        getCollectorOnCase(props.case.type, props.case.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId)) ||
        undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const contactPerson = getContactPerson(props.customer);
    const toggleEBoksPreviewModal = () => {
        setEBoksPreviewModalOpen(!eboksPreviewModalOpen);
    };
    const toggleMentionExplanation = () => {
        setMentionExplanationModalOpen(!mentionExplanationModalOpen);
    };
    const hasBody = () => {
        return !!(content &&
            content.getCurrentContent() &&
            content.getCurrentContent().getPlainText());
    };
    const downloadUrl = `${APIBASE}/collection-engine/templates/file${templatesAPI.genLetterUrl(eboks.templateId, props.referenceId, {
        body: parseText(toHtml(content), mentionSuggestions),
        caseId: props.referenceId,
    }, DEFAULT_FILE_NAME)}`;
    const recipients = genRecipientsRaw(props.customer, {
        KVHX: props.KVHX,
    });
    let recipient = findRecipient(recipients, props.customer);
    const unlikelyToReceive = recipients.length > 0 && recipient == null;
    if (recipient == null && recipients.length > 0) {
        recipient = recipients[0];
    }
    const sendEBoks = async () => {
        try {
            if (!collector)
                return alert('Could not find collector');
            const pdfBody = parseText(toHtml(content), mentionSuggestions);
            const eBoksTitle = getSubjectContent(parseText(toHtml(subject), mentionSuggestions));
            if (!recipient)
                throw 'No recipient';
            cancelDebounce('e-boks-content');
            cancelDebounce('e-boks-subject');
            setReset(true);
            c.newInteractions.sendEBoks(props.referenceId, Entity.Case, eboks.templateId, pdfBody, eBoksTitle, visibleToClients ? 'client' : 'caseworker', eboks.intent, recipient);
        }
        catch (error) {
            console.error(error);
            alert('Error');
        }
    };
    const deliverabilityUpdater = useMutation(() => {
        const contact = getContactPerson(props.customer);
        if (contact == null) {
            throw new Error('No contact person');
        }
        const deliverability = contact.details.deliverability ?? {};
        const eboks = deliverability.eboks ?? [];
        const newEboks = eboks.concat(recipients.map((r) => {
            return {
                timestamp: new Date().toISOString(),
                recipient: r,
                success: true,
                manualOverride: true,
            };
        }));
        patchUser(props.customer.customerId, contact.userId, {
            details: {
                deliverability: { ...deliverability, eboks: newEboks },
            },
        });
    }, {
        onSuccess: () => {
            q.invalidate(keys.write.modify(customersEntity, props.customer.customerId));
        },
    });
    return (React.createElement(React.Fragment, null,
        unlikelyToReceive ? (React.createElement(Disclaimer, null,
            lang.interactions.unlikelyToReceive,
            React.createElement(Button, { onClick: () => {
                    deliverabilityUpdater.mutate();
                } }, "Fjern markering"))) : null,
        React.createElement(TemplateSearch, { interaction: eboks, templateType: TemplateType.EBOKS, case: props.case, customer: props.customer, onChange: (state, subject) => {
                setContent(state);
                if (subject != null) {
                    setSubject(subject);
                }
            } }),
        React.createElement(Input, { disabled: true, placeholder: lang.interactions.contact, value: recipient
                ? recipient.type === EBoksRecipientType.KVHX
                    ? `${recipient.firstName} ${recipient.lastName} (${contactPerson && contactPerson.details.address
                        ? addressToString(contactPerson.details.address, {
                            noCoAddress: true,
                            noCountry: true,
                        })
                        : ''})`
                    : recipient.type === EBoksRecipientType.CentralRegisterId
                        ? `${recipient.isCompany ? 'CVR' : 'CPR'}: ${recipient.centralRegisterId}`
                        : ''
                : '' }),
        React.createElement(Editor, { wrapperClassName: "eboks-subject-editor-wrapper style-as-input-field", 
            // TODO: Lang
            placeholder: "Subject", toolbarHidden: true, editorState: subject, onEditorStateChange: (newEditorState) => {
                setSubject(newEditorState);
                debounce('e-boks-subject', () => {
                    c.newInteractions.changeEBoksSubjectEditorState(props.referenceId, newEditorState);
                }, 1000);
            }, stripPastedStyles: true, mention: props.referenceType === Entity.Case
                ? {
                    separator: ' ',
                    trigger: '@',
                    suggestions: mentionSuggestions.map((suggestion) => ({
                        value: suggestion.label,
                        text: suggestion.label,
                        url: suggestion.label,
                    })),
                }
                : undefined }),
        React.createElement(Editor, { toolbar: { options: EditorToolbarOptions }, editorState: content, onEditorStateChange: (newEditorState) => {
                setContent(newEditorState);
                debounce('e-boks-content', () => {
                    c.newInteractions.changeEditorState(props.referenceId, 'eboks', newEditorState);
                }, 1000);
            }, stripPastedStyles: true, mention: {
                separator: ' ',
                trigger: '@',
                suggestions: mentionSuggestions.map((suggestion) => ({
                    value: suggestion.label,
                    text: suggestion.label,
                    url: suggestion.label,
                })),
            } }),
        props.isDebtor && (React.createElement("div", { style: { margin: '1em 0 0 0' } },
            React.createElement(Intent, { intent: eboks.intent, editable: true, onEdit: (intent) => {
                    c.newInteractions.changeField(props.referenceId, 'eboks', 'intent', intent);
                } }))),
        React.createElement(Checkbox, { checked: visibleToClients, onChange: (e) => setVisibleToClients(e.target.checked), placeholder: lang.interactions.visibilityCheckText }),
        React.createElement("br", null),
        React.createElement(Button, { size: "small", loading: eboks.loading, disabled: !eboks.templateId, onClick: sendEBoks }, lang.interactions.sendEBoks),
        ' ',
        React.createElement(Button, { size: "small", theme: "secondary", loading: eboks.loading, disabled: !eboks.templateId || !hasBody(), onClick: toggleEBoksPreviewModal }, lang.interactions.preview),
        React.createElement(Button, { size: "small", theme: "secondary", style: { float: 'right', marginRight: '5px' }, onClick: toggleMentionExplanation },
            React.createElement(Info, null)),
        eboksPreviewModalOpen && (React.createElement(LetterPreview, { onToggle: toggleEBoksPreviewModal, downloadUrl: downloadUrl })),
        mentionExplanationModalOpen && (React.createElement(Modal, { onToggle: toggleMentionExplanation, header: lang.interactions.explanation },
            React.createElement(MentionExplanation, { suggestions: mentionSuggestions })))));
};
export default EBoksInteraction;
//# sourceMappingURL=EBoksInteraction.js.map